/**
 * The five settings sections, as markup.
 *
 * Pure string builders with no DOM and no network, for the same reason
 * `insurance-panel.ts` is: the interesting states here are the ones that are
 * awkward to reach by hand — a rejected verification, a payout blocked on KYC,
 * an empty payment history, a preview build with withdrawals switched off — and
 * a builder that takes state and returns markup can be tested at all of them in
 * a millisecond.
 *
 * Wiring lives in `settings-modal.ts`.
 */

import { esc } from '../item-icon';
import { getIntlLocale, N_, t } from '../../i18n/i18n';
import {
  MOUSE_SENSITIVITY_MAX,
  MOUSE_SENSITIVITY_MIN,
  type AudioSettings,
  type GameplaySettings,
  type KycStatus,
  type InvoiceRow,
  type PaymentEntry,
  type SettingsOverview,
  type WithdrawalOverview,
} from './settings-types';

export type SectionId = 'profile' | 'audio' | 'gameplay' | 'payments' | 'invoices' | 'withdrawal';

export const SECTIONS: ReadonlyArray<{ id: SectionId; label: string }> = [
  { id: 'profile', label: N_('Profile') },
  { id: 'audio', label: N_('Audio') },
  { id: 'gameplay', label: N_('Gameplay') },
  { id: 'payments', label: N_('Payment History') },
  { id: 'invoices', label: N_('Invoices') },
  { id: 'withdrawal', label: N_('Withdrawal') },
];

/**
 * Minor units to a readable amount. Never a float in, never a float out.
 * Digits are grouped for the account's format locale, or the interface
 * language when none is saved; the currency code stays as a code.
 */
export function formatMoney(cents: number, currency: string): string {
  const sign = cents < 0 ? '-' : '';
  const amount = (Math.abs(cents) / 100).toLocaleString(formatLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}${amount} ${esc(currency)}`;
}

/**
 * The account's saved locale as a BCP 47 tag, or undefined for "match my
 * browser". The same setting picks the interface language (engine/i18n); when
 * it is blank, dates and numbers follow whatever language the interface
 * resolved to, so the two never disagree.
 */
let displayLocale: string | undefined;

/** The tag dates and money are written in: the saved one, else the interface's. */
function formatLocale(): string {
  return displayLocale ?? getIntlLocale();
}

/** Adopt the profile's `locale` (`ru_RU`). Unknown or blank falls back to the browser. */
export function setDisplayLocale(locale: string | null | undefined): void {
  const tag = (locale ?? '').replace('_', '-');
  try {
    displayLocale = tag && Intl.DateTimeFormat.supportedLocalesOf([tag]).length > 0 ? tag : undefined;
  } catch {
    // A malformed tag throws RangeError; the interface language is the honest answer.
    displayLocale = undefined;
  }
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(formatLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

const KYC_COPY: Record<KycStatus, { label: string; tone: 'dim' | 'warn' | 'good' | 'bad'; body: string }> = {
  NONE: {
    label: N_('Not verified'),
    tone: 'dim',
    body: N_('Verification is only needed before a payout. Nothing else in the game asks for it.'),
  },
  PENDING: {
    label: N_('Under review'),
    tone: 'warn',
    body: N_('Submitted. Somebody will look at it; you do not need to do anything else.'),
  },
  APPROVED: {
    label: N_('Verified'),
    tone: 'good',
    body: N_('Your identity is confirmed. Payout requests are open.'),
  },
  REJECTED: {
    label: N_('Rejected'),
    tone: 'bad',
    body: N_('The last submission was not accepted. You can submit again.'),
  },
};

/**
 * 2026-10-19: the APPROVED copy promised "Payout requests are open" even with
 * payouts switched off by the operator — a promise the Withdrawal tab (hidden)
 * could not keep.
 */
function kycBody(o: SettingsOverview): string {
  if (o.kyc.status === 'APPROVED' && !o.features.withdrawals) return t('Your identity is confirmed.');
  return t(KYC_COPY[o.kyc.status].body);
}

function statusChip(status: KycStatus): string {
  const { label, tone } = KYC_COPY[status];
  const cls =
    tone === 'good' ? 'chip chip-gold'
    : tone === 'bad' ? 'chip text-error'
    : tone === 'warn' ? 'chip text-warn'
    : 'chip';
  return `<span class="${cls} text-[12px]">${esc(t(label))}</span>`;
}

/**
 * Status words the server sends as enums (payments, invoices, payout
 * requests). Known ones are shown translated; anything new is shown as sent.
 */
const STATUS_LABELS: Record<string, string> = {
  PENDING: N_('Pending'),
  PROCESSING: N_('Processing'),
  COMPLETED: N_('Completed'),
  PAID: N_('Paid'),
  APPROVED: N_('Approved'),
  REJECTED: N_('Rejected'),
  FAILED: N_('Failed'),
  CANCELLED: N_('Cancelled'),
  CANCELED: N_('Cancelled'),
  EXPIRED: N_('Expired'),
  REFUNDED: N_('Refunded'),
  PARTIALLY_REFUNDED: N_('Partially refunded'),
  ISSUED: N_('Issued'),
  VOID: N_('Void'),
  MISMATCH: N_('Under review'),
};

export function statusLabel(status: string): string {
  const known = STATUS_LABELS[status.toUpperCase()];
  return known ? t(known) : status;
}

/** A country's name in the interface language; the English table is the fallback. */
function countryName(code: string, english: string, names: Intl.DisplayNames | null): string {
  try {
    return names?.of(code) ?? english;
  } catch {
    return english;
  }
}

function regionNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([getIntlLocale()], { type: 'region', fallback: 'none' });
  } catch {
    return null;
  }
}

function field(id: string, label: string, input: string, hint = ''): string {
  // content-start: in a two/three-column row the cells stretch to the tallest
  // one, and a grid label would spread that slack between its rows — pushing
  // a hint-less control below its neighbour (QA 2026-10-15, Language/Currency
  // and Country/City). Packing rows to the top keeps every control on one line.
  return `
    <label class="grid content-start gap-1.5" for="${id}">
      <span class="eyebrow eyebrow-dim">${esc(label)}</span>
      ${input}
      ${hint ? `<span class="text-[12px] text-text-dim">${hint}</span>` : ''}
    </label>`;
}

const INPUT_CLASS =
  'w-full min-h-11 px-3 bg-surface-2 text-text border border-border rounded-sm '
  + 'focus:outline-none focus:border-gold placeholder:text-text-dim/70';

const COUNTRIES: ReadonlyArray<[code: string, name: string]> = [
  ['AD','Andorra'],['AE','United Arab Emirates'],['AF','Afghanistan'],['AG','Antigua and Barbuda'],
  ['AL','Albania'],['AM','Armenia'],['AO','Angola'],['AR','Argentina'],['AT','Austria'],
  ['AU','Australia'],['AZ','Azerbaijan'],['BA','Bosnia and Herzegovina'],['BB','Barbados'],
  ['BD','Bangladesh'],['BE','Belgium'],['BF','Burkina Faso'],['BG','Bulgaria'],['BH','Bahrain'],
  ['BI','Burundi'],['BJ','Benin'],['BN','Brunei'],['BO','Bolivia'],['BR','Brazil'],
  ['BS','Bahamas'],['BT','Bhutan'],['BW','Botswana'],['BY','Belarus'],['BZ','Belize'],
  ['CA','Canada'],['CD','Congo (DRC)'],['CF','Central African Republic'],['CG','Congo'],
  ['CH','Switzerland'],['CI','Côte d\'Ivoire'],['CL','Chile'],['CM','Cameroon'],['CN','China'],
  ['CO','Colombia'],['CR','Costa Rica'],['CU','Cuba'],['CV','Cape Verde'],['CY','Cyprus'],
  ['CZ','Czechia'],['DE','Germany'],['DJ','Djibouti'],['DK','Denmark'],['DM','Dominica'],
  ['DO','Dominican Republic'],['DZ','Algeria'],['EC','Ecuador'],['EE','Estonia'],['EG','Egypt'],
  ['ER','Eritrea'],['ES','Spain'],['ET','Ethiopia'],['FI','Finland'],['FJ','Fiji'],['FR','France'],
  ['GA','Gabon'],['GB','United Kingdom'],['GD','Grenada'],['GE','Georgia'],['GH','Ghana'],
  ['GM','Gambia'],['GN','Guinea'],['GQ','Equatorial Guinea'],['GR','Greece'],['GT','Guatemala'],
  ['GW','Guinea-Bissau'],['GY','Guyana'],['HK','Hong Kong'],['HN','Honduras'],['HR','Croatia'],
  ['HT','Haiti'],['HU','Hungary'],['ID','Indonesia'],['IE','Ireland'],['IL','Israel'],
  ['IN','India'],['IQ','Iraq'],['IR','Iran'],['IS','Iceland'],['IT','Italy'],['JM','Jamaica'],
  ['JO','Jordan'],['JP','Japan'],['KE','Kenya'],['KG','Kyrgyzstan'],['KH','Cambodia'],
  ['KI','Kiribati'],['KM','Comoros'],['KN','Saint Kitts and Nevis'],['KP','North Korea'],
  ['KR','South Korea'],['KW','Kuwait'],['KZ','Kazakhstan'],['LA','Laos'],['LB','Lebanon'],
  ['LC','Saint Lucia'],['LI','Liechtenstein'],['LK','Sri Lanka'],['LR','Liberia'],['LS','Lesotho'],
  ['LT','Lithuania'],['LU','Luxembourg'],['LV','Latvia'],['LY','Libya'],['MA','Morocco'],
  ['MC','Monaco'],['MD','Moldova'],['ME','Montenegro'],['MG','Madagascar'],['MK','North Macedonia'],
  ['ML','Mali'],['MM','Myanmar'],['MN','Mongolia'],['MO','Macao'],['MR','Mauritania'],
  ['MT','Malta'],['MU','Mauritius'],['MV','Maldives'],['MW','Malawi'],['MX','Mexico'],
  ['MY','Malaysia'],['MZ','Mozambique'],['NA','Namibia'],['NE','Niger'],['NG','Nigeria'],
  ['NI','Nicaragua'],['NL','Netherlands'],['NO','Norway'],['NP','Nepal'],['NR','Nauru'],
  ['NZ','New Zealand'],['OM','Oman'],['PA','Panama'],['PE','Peru'],['PG','Papua New Guinea'],
  ['PH','Philippines'],['PK','Pakistan'],['PL','Poland'],['PT','Portugal'],['PW','Palau'],
  ['PY','Paraguay'],['QA','Qatar'],['RO','Romania'],['RS','Serbia'],['RU','Russia'],
  ['RW','Rwanda'],['SA','Saudi Arabia'],['SB','Solomon Islands'],['SC','Seychelles'],
  ['SD','Sudan'],['SE','Sweden'],['SG','Singapore'],['SI','Slovenia'],['SK','Slovakia'],
  ['SL','Sierra Leone'],['SM','San Marino'],['SN','Senegal'],['SO','Somalia'],['SR','Suriname'],
  ['SS','South Sudan'],['ST','São Tomé and Príncipe'],['SV','El Salvador'],['SY','Syria'],
  ['SZ','Eswatini'],['TD','Chad'],['TG','Togo'],['TH','Thailand'],['TJ','Tajikistan'],
  ['TL','Timor-Leste'],['TM','Turkmenistan'],['TN','Tunisia'],['TO','Tonga'],['TR','Türkiye'],
  ['TT','Trinidad and Tobago'],['TV','Tuvalu'],['TW','Taiwan'],['TZ','Tanzania'],['UA','Ukraine'],
  ['UG','Uganda'],['US','United States'],['UY','Uruguay'],['UZ','Uzbekistan'],
  ['VA','Vatican City'],['VC','Saint Vincent and the Grenadines'],['VE','Venezuela'],
  ['VN','Vietnam'],['VU','Vanuatu'],['WS','Samoa'],['XK','Kosovo'],['YE','Yemen'],
  ['ZA','South Africa'],['ZM','Zambia'],['ZW','Zimbabwe'],
];

function countrySelect(id: string, current: string | null): string {
  const val = (current ?? '').toUpperCase();
  const names = regionNames();
  const locale = getIntlLocale();
  const options = COUNTRIES
    .map(([code, english]) => [code, countryName(code, english, names)] as const)
    .sort((a, b) => a[1].localeCompare(b[1], locale))
    .map(([code, name]) =>
      `<option value="${code}"${code === val ? ' selected' : ''}>${esc(name)}</option>`
    ).join('');
  return `<select id="${id}" class="${INPUT_CLASS}"><option value="">${esc(t('Select country'))}</option>${options}</select>`;
}

export function profileSection(o: SettingsOverview): string {
  return `
    <div class="grid gap-6 max-w-[620px]">
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Account'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          ${field('set-username', t('Display name'),
            `<input id="set-username" class="${INPUT_CLASS}" type="text" maxlength="32" autocomplete="username"
                    value="${esc(o.profile.username ?? '')}" placeholder="${esc(t('Unnamed delver'))}" />`,
            esc(t('Letters, digits, underscore and hyphen. Three characters or more.')))}
          <div class="grid sm:grid-cols-2 gap-3">
            ${field('set-locale', t('Language'),
              `<select id="set-locale" class="${INPUT_CLASS}">
                 ${localeOptions(o.profile.locale)}
               </select>`,
              esc(t('The interface, dates and numbers.')))}
            ${field('set-currency', t('Currency'),
              `<select id="set-currency" class="${INPUT_CLASS}">
                 ${currencyOptions(o.profile.currency)}
               </select>`,
              esc(t('What prices and invoices are shown in.')))}
          </div>
          <div class="flex items-center gap-2 pt-1">
            <button id="set-profile-save" type="button" class="btn btn-gold px-5">${esc(t('Save'))}</button>
            <span id="set-profile-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
          </div>
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Billing Details'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          <div class="grid sm:grid-cols-2 gap-3">
            ${field('set-fname', t('First name'),
              `<input id="set-fname" class="${INPUT_CLASS}" type="text" maxlength="64"
                      value="${esc(o.profile.firstName ?? '')}" />`)}
            ${field('set-lname', t('Last name'),
              `<input id="set-lname" class="${INPUT_CLASS}" type="text" maxlength="64"
                      value="${esc(o.profile.lastName ?? '')}" />`)}
          </div>
          ${field('set-billing-street', t('Street'),
            `<input id="set-billing-street" class="${INPUT_CLASS}" type="text" maxlength="255"
                    value="${esc(o.profile.billing.street ?? '')}" />`)}
          <div class="grid sm:grid-cols-3 gap-3">
            ${field('set-billing-city', t('City'),
              `<input id="set-billing-city" class="${INPUT_CLASS}" type="text" maxlength="128"
                      value="${esc(o.profile.billing.city ?? '')}" />`)}
            ${field('set-billing-zip', t('ZIP / Postal'),
              `<input id="set-billing-zip" class="${INPUT_CLASS}" type="text" maxlength="20"
                      value="${esc(o.profile.billing.zip ?? '')}" />`)}
            ${field('set-billing-country', t('Country'),
              countrySelect('set-billing-country', o.profile.billing.country))}
          </div>
          <div class="flex items-center gap-2 pt-1">
            <button id="set-billing-save" type="button" class="btn btn-gold px-5">${esc(t('Save'))}</button>
            <span id="set-billing-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
          </div>
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Email'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          <p class="text-[13px] text-text-dim">
            ${t('Currently {email}', { email: `<strong class="text-text">${esc(o.profile.email)}</strong>` })}
            ${o.profile.emailVerified
              ? `<span class="chip chip-gold text-[12px] ml-1">${esc(t('Verified'))}</span>`
              : `<span class="chip text-warn text-[12px] ml-1">${esc(t('Unverified'))}</span>`}
          </p>
          ${o.profile.pendingEmail
            ? `<div class="notice-warn text-[13px]">
                 ${t('Waiting on confirmation from {email}.', { email: `<strong>${esc(o.profile.pendingEmail)}</strong>` })}
                 ${esc(t('Nothing changes until that link is opened, and your current address keeps working.'))}
               </div>`
            : ''}
          ${field('set-email', t('New address'),
            `<input id="set-email" class="${INPUT_CLASS}" type="email" autocomplete="email" placeholder="you@example.com" />`)}
          ${field('set-email-password', t('Your password'),
            `<input id="set-email-password" class="${INPUT_CLASS}" type="password" autocomplete="current-password" />`,
            esc(t('Being signed in is not enough to move an account.')))}
          <div class="flex items-center gap-2 pt-1">
            <button id="set-email-save" type="button" class="btn btn-stone px-5">${esc(t('Send confirmation'))}</button>
            <span id="set-email-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
          </div>
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Password'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          ${field('set-pw-current', t('Current password'),
            `<input id="set-pw-current" class="${INPUT_CLASS}" type="password" autocomplete="current-password" />`)}
          ${field('set-pw-new', t('New password'),
            `<input id="set-pw-new" class="${INPUT_CLASS}" type="password" autocomplete="new-password" minlength="8" />`,
            esc(t('Eight characters or more.')))}
          <div class="flex items-center gap-2 pt-1">
            <button id="set-pw-save" type="button" class="btn btn-stone px-5">${esc(t('Change password'))}</button>
            <span id="set-pw-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
          </div>
          <p class="text-[12px] text-text-dim">${esc(t('Changing it signs out every other device.'))}</p>
        </div>
      </section>

      ${o.features.kyc ? identitySection(o) : ''}
    </div>`;
}

/**
 * Only drawn when verification is switched on (`features.kyc`). 2026-10-19:
 * with it off the section used to stay, badge and all, above a line saying it
 * was off; core no longer asks for it at payout, so there is nothing to show.
 */
function identitySection(o: SettingsOverview): string {
  return `
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold flex items-center gap-2">${esc(t('Identity'))} ${statusChip(o.kyc.status)}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          <p class="text-[13px] text-text-dim">${esc(kycBody(o))}</p>
          ${o.kyc.status === 'REJECTED' && o.kyc.rejectReason
            ? `<div class="notice-error text-[13px]">${esc(o.kyc.rejectReason)}</div>`
            : ''}
          ${o.kyc.status === 'APPROVED' || o.kyc.status === 'PENDING'
            ? `<dl class="grid grid-cols-2 gap-2 text-[13px]">
                 <dt class="text-text-dim">${esc(t('Name'))}</dt><dd>${esc(o.kyc.fullName ?? '—')}</dd>
                 <dt class="text-text-dim">${esc(t('Country'))}</dt><dd>${esc(o.kyc.country ?? '—')}</dd>
                 <dt class="text-text-dim">${esc(t('Submitted'))}</dt><dd>${formatDate(o.kyc.submittedAt)}</dd>
               </dl>`
            : kycForm()}
        </div>
      </section>`;
}

function kycForm(): string {
  return `
    ${field('set-kyc-name', t('Full legal name'),
      `<input id="set-kyc-name" class="${INPUT_CLASS}" type="text" maxlength="120" autocomplete="name" />`)}
    <div class="grid sm:grid-cols-2 gap-3">
      ${field('set-kyc-dob', t('Date of birth'),
        `<input id="set-kyc-dob" class="${INPUT_CLASS}" type="date" autocomplete="bday" />`,
        esc(t('You must be 18 or over.')))}
      ${field('set-kyc-country', t('Country'),
        countrySelect('set-kyc-country', null))}
    </div>
    ${field('set-kyc-address', t('Address'),
      `<input id="set-kyc-address" class="${INPUT_CLASS}" type="text" maxlength="300" autocomplete="street-address" />`)}
    <div class="flex items-center gap-2 pt-1">
      <button id="set-kyc-save" type="button" class="btn btn-stone px-5">${esc(t('Submit for review'))}</button>
      <span id="set-kyc-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
    </div>`;
}

/**
 * Every language is listed by its own name (an endonym), so a player who
 * cannot read the current interface can still find theirs. Only the blank
 * "follow the browser" entry is translated.
 */
const LOCALES: ReadonlyArray<[string, string]> = [
  ['', N_('Match my browser')],
  ['en_GB', 'English (UK)'],
  ['en_US', 'English (US)'],
  ['et_EE', 'Eesti'],
  ['de_DE', 'Deutsch'],
  ['fr_FR', 'Français'],
  ['es_ES', 'Español'],
  ['pl_PL', 'Polski'],
  ['ru_RU', 'Русский'],
];

const CURRENCIES: ReadonlyArray<[string, string]> = [
  ['', N_('Match my region')],
  ['EUR', N_('Euro')],
  ['USD', N_('US dollar')],
  ['GBP', N_('Pound sterling')],
  ['PLN', N_('Złoty')],
];

function options(
  list: ReadonlyArray<[string, string]>,
  selected: string | null,
  label: (value: string, english: string) => string,
): string {
  return list
    .map(([value, english]) =>
      `<option value="${esc(value)}"${(selected ?? '') === value ? ' selected' : ''}>${esc(label(value, english))}</option>`)
    .join('');
}

// Language names stay endonyms; only the blank entry goes through t().
export const localeOptions = (selected: string | null) =>
  options(LOCALES, selected, (value, english) => (value ? english : t(english)));
export const currencyOptions = (selected: string | null) =>
  options(CURRENCIES, selected, (_value, english) => t(english));

// ─── Audio ───────────────────────────────────────────────────────────────────

/** One bus per slider, matching the mix buses in `config/audio-tuning.ts`. */
const AUDIO_ROWS: ReadonlyArray<{ key: keyof AudioSettings; label: string; hint: string }> = [
  { key: 'master', label: N_('Master'), hint: N_('Everything, including music.') },
  { key: 'sfx', label: N_('Effects'), hint: N_('Swings, impacts, footsteps, doors.') },
  { key: 'ambient', label: N_('Ambience'), hint: N_('The room you are standing in.') },
  { key: 'ui', label: N_('Interface'), hint: N_('Clicks, loot, the level-up fanfare.') },
  { key: 'music', label: N_('Music'), hint: N_('The bed in the Sconce and on the way in.') },
];

export function audioSection(a: AudioSettings): string {
  const sliders = AUDIO_ROWS.map((row) => {
    const value = Math.round((a[row.key] as number) * 100);
    return `
      <div class="grid gap-1.5" data-audio-row="${row.key}">
        <div class="flex items-baseline justify-between gap-3">
          <label class="eyebrow eyebrow-dim" for="set-vol-${row.key}">${esc(t(row.label))}</label>
          <output class="text-[13px] tabular-nums text-gold" id="set-vol-${row.key}-out" for="set-vol-${row.key}">${percent(value)}</output>
        </div>
        <input id="set-vol-${row.key}" data-volume="${row.key}" type="range" min="0" max="100" step="1"
               value="${value}" class="settings-range" aria-describedby="set-vol-${row.key}-hint" />
        <span id="set-vol-${row.key}-hint" class="text-[12px] text-text-dim">${esc(t(row.hint))}</span>
      </div>`;
  }).join('');

  return `
    <div class="grid gap-6 max-w-[560px]">
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Volume'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-4">${sliders}</div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Music'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          ${toggleRow('set-music-enabled', 'musicEnabled', t('Play music'), a.musicEnabled,
            t('Off is remembered separately from the volume, so turning it back on restores the level you set.'))}
        </div>
      </section>

      <p class="text-[13px] text-text-dim max-w-[52ch]">
        ${esc(t('Hearing is a skill in this game. The effects bus carries every sound on the floor — footsteps, a chest lid, the dead coming closer — so turning it down costs you information nothing on screen replaces.'))}
      </p>
    </div>`;
}

/** `42%` in the interface language (some write `42 %`). */
export function percent(value: number): string {
  return (value / 100).toLocaleString(getIntlLocale(), { style: 'percent', maximumFractionDigits: 0 });
}

function toggleRow(id: string, key: string, label: string, on: boolean, hint: string): string {
  return `
    <div class="flex items-start gap-3">
      <button id="${id}" type="button" role="switch" aria-checked="${on}" data-toggle="${key}"
        class="settings-switch shrink-0 mt-0.5" aria-labelledby="${id}-label">
        <span class="settings-switch-knob"></span>
      </button>
      <div class="grid gap-0.5">
        <span id="${id}-label" class="text-[15px]">${esc(label)}</span>
        <span class="text-[12px] text-text-dim">${esc(hint)}</span>
      </div>
    </div>`;
}

// ─── Gameplay ────────────────────────────────────────────────────────────────

export function gameplaySection(g: GameplaySettings): string {
  const sens = g.mouseSensitivity.toLocaleString(getIntlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `
    <div class="grid gap-6 max-w-[560px]">
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Camera'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-4">
          <div class="grid gap-1.5">
            <div class="flex items-baseline justify-between gap-3">
              <label class="eyebrow eyebrow-dim" for="set-sens">${esc(t('Mouse sensitivity'))}</label>
              <output class="text-[13px] tabular-nums text-gold" id="set-sens-out" for="set-sens">${sens}&times;</output>
            </div>
            <input id="set-sens" type="range" class="settings-range"
                   min="${MOUSE_SENSITIVITY_MIN * 100}" max="${MOUSE_SENSITIVITY_MAX * 100}" step="5"
                   value="${Math.round(g.mouseSensitivity * 100)}" />
            <span class="text-[12px] text-text-dim">
              ${esc(t('A duel is decided by where you are facing. Vertical is already slower than horizontal on purpose; this scales both.'))}
            </span>
          </div>
          ${toggleRow('set-invert', 'invertLook', t('Invert vertical look'), g.invertLook,
            t('Pull down to look up.'))}
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('During a run'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3.5">
          ${toggleRow('set-damage-numbers', 'damageNumbers', t('Damage numbers'), g.damageNumbers,
            t('The figures that float off what you hit.'))}
          ${toggleRow('set-hit-flash', 'hitFlash', t('Screen flash when hit'), g.hitFlash,
            t('The red edge when the damage is yours. Off makes a fight quieter to read.'))}
          ${toggleRow('set-skip-flythrough', 'autoSkipFlyThrough', t('Skip the route fly-through'), g.autoSkipFlyThrough,
            t('It previews the way out before you gain control. Any input skips it anyway.'))}
          ${toggleRow('set-perf', 'showPerformance', t('Show performance overlay'), g.showPerformance,
            t('Frame time and draw calls. Useful when something is wrong and you want to say what.'))}
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Guidance'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          <div class="grid gap-0.5">
            <span class="text-[15px]">${esc(t('The tutorial'))}</span>
            <span class="text-[12px] text-text-dim">
              ${esc(t('The tour of the Sconce and the prompts underground. Skipping it is never permanent — this is where it comes back.'))}
            </span>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <button id="set-tutorial-replay" type="button" class="btn btn-stone min-h-11 px-4 text-[13px]">
              ${esc(t('Replay the tutorial'))}
            </button>
            <span id="set-tutorial-note" class="text-[12px] text-text-dim" role="status"></span>
          </div>
        </div>
      </section>
    </div>`;
}

// ─── Payment history ─────────────────────────────────────────────────────────

export function paymentsSection(entries: PaymentEntry[] | null, invoicesEnabled: boolean): string {
  if (entries === null) return skeleton(t('Loading your history…'));

  if (entries.length === 0) {
    return emptyState(
      t('Nothing bought yet'),
      invoicesEnabled
        ? t('Ember bundles and energy show up here the moment they are paid for, with an invoice you can download.')
        : t('Purchases are switched off on this build, so there is nothing to list.'),
    );
  }

  const rows = entries.map((e) => `
    <tr class="border-t border-border/60">
      <td class="py-2.5 pr-3 whitespace-nowrap text-text-dim tabular-nums">${formatDate(e.at)}</td>
      <td class="py-2.5 pr-3">
        ${esc(e.description === 'Purchase' ? t('Purchase') : e.description)}
        ${e.provider ? `<span class="text-[12px] text-text-dim block">${esc(e.provider)}</span>` : ''}
      </td>
      <td class="py-2.5 pr-3 whitespace-nowrap tabular-nums ${e.amountCents < 0 ? 'text-success' : 'text-text'}">
        ${formatMoney(e.amountCents, e.currency)}
      </td>
      <td class="py-2.5 pr-3 whitespace-nowrap"><span class="chip text-[12px]">${esc(statusLabel(e.status))}</span></td>
      <td class="py-2.5 text-right whitespace-nowrap">
        ${e.invoiceNumber && invoicesEnabled
          ? `<a class="btn btn-quiet px-3 text-[12px]" download
                href="${invoicePdfHref(e.invoiceNumber)}">${esc(t('Invoice'))}</a>`
          : ''}
      </td>
    </tr>`).join('');

  return `
    <div class="grid gap-3">
      <h3 class="font-heading text-lg text-gold">${esc(t('Payment History'))}</h3>
      <div class="frame-pewter p-1 overflow-x-auto">
        <table class="w-full min-w-[520px] text-[14px] border-collapse">
          <thead>
            <tr class="text-left">
              <th scope="col" class="py-2 px-3 eyebrow eyebrow-dim font-normal">${esc(t('Date'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('What'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Amount'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Status'))}</th>
              <th scope="col" class="py-2 eyebrow eyebrow-dim font-normal"><span class="sr-only">${esc(t('Invoice'))}</span></th>
            </tr>
          </thead>
          <tbody class="px-3">${rows}</tbody>
        </table>
      </div>
    </div>`;
}

// ─── Invoices ────────────────────────────────────────────────────────────────

/**
 * The PDF for one invoice.
 *
 * Points at the game's renderer rather than core's `/download`: same document,
 * the game's typeface and colours. `theme=print` is the parchment version —
 * see `invoice-theme.ts` on why both exist.
 */
export function invoicePdfHref(number: string, theme?: 'print'): string {
  const q = theme ? `?theme=${theme}` : '';
  return `/api/invoices/${encodeURIComponent(number)}/pdf${q}`;
}

/**
 * Every invoice, both directions.
 *
 * Separate from Payment History on purpose. That list answers "what did I
 * buy"; this one answers "where is the paperwork", and the two have different
 * shapes — a withdrawal is not a payment and would never appear in the first
 * list, but it absolutely produces an invoice.
 *
 * Each row offers the game-styled PDF, with the print version as a quiet
 * second link rather than a choice a player has to make first.
 */
export function invoicesSection(rows: InvoiceRow[] | null, enabled: boolean): string {
  if (!enabled) {
    return emptyState(
      t('Invoices are off on this build'),
      t('Nothing here charges money, so there is no paperwork to issue.'),
    );
  }
  if (rows === null) return skeleton(t('Loading your invoices…'));
  if (rows.length === 0) {
    return emptyState(
      t('No invoices yet'),
      t('One is issued for every deposit and every payout, and lands here the moment it exists.'),
    );
  }

  const body = rows.map((row) => {
    const deposit = row.type === 'IN';
    return `
    <tr class="border-t border-border/60">
      <td class="py-2.5 px-3 whitespace-nowrap tabular-nums">${esc(row.number)}</td>
      <td class="py-2.5 pr-3 whitespace-nowrap text-text-dim tabular-nums">${formatDate(row.createdAt)}</td>
      <td class="py-2.5 pr-3 whitespace-nowrap ${deposit ? 'text-success' : 'text-error'}">
        ${esc(deposit ? t('Deposit') : t('Withdrawal'))}
      </td>
      <td class="py-2.5 pr-3">${esc(row.description)}</td>
      <td class="py-2.5 pr-3 whitespace-nowrap tabular-nums">${formatMoney(row.totalCents, row.currency)}</td>
      <td class="py-2.5 pr-3 whitespace-nowrap"><span class="chip text-[12px]">${esc(statusLabel(row.status))}</span></td>
      <td class="py-2.5 pr-3 text-right whitespace-nowrap">
        <a class="btn btn-stone px-3 text-[12px]" target="_blank" rel="noopener"
           href="${invoicePdfHref(row.number)}">${esc(t('PDF'))}</a>
        <a class="block mt-1 text-[11px] text-text-dim underline decoration-dotted" target="_blank" rel="noopener"
           href="${invoicePdfHref(row.number, 'print')}">${esc(t('print version'))}</a>
      </td>
    </tr>`;
  }).join('');

  return `
    <div class="grid gap-3">
      <h3 class="font-heading text-lg text-gold">${esc(t('Invoices'))}</h3>
      <p class="text-[13px] text-text-dim">${esc(t('One for every deposit and every payout. The PDF carries your details and ours, and opens in a new tab — save it from there.'))}</p>
      <div class="frame-pewter p-1 overflow-x-auto">
        <table class="w-full min-w-[680px] text-[14px] border-collapse">
          <thead>
            <tr class="text-left">
              <th scope="col" class="py-2 px-3 eyebrow eyebrow-dim font-normal">${esc(t('Number'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Date'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Direction'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('What'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Total'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Status'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal"><span class="sr-only">${esc(t('Download'))}</span></th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

// ─── Withdrawal ──────────────────────────────────────────────────────────────

export function withdrawalSection(w: WithdrawalOverview | null): string {
  if (w === null) return skeleton(t('Loading…'));

  const spendable = Math.max(0, w.availableCents - w.pendingCents);
  const blocked = withdrawalBlockReason(w, spendable);


  const history = w.requests.length === 0 ? '' : `
    <section class="grid gap-3">
      <h3 class="font-heading text-lg text-gold">${esc(t('Requests'))}</h3>
      <div class="frame-pewter p-1 overflow-x-auto">
        <table class="w-full min-w-[460px] text-[14px] border-collapse">
          <thead>
            <tr class="text-left">
              <th scope="col" class="py-2 px-3 eyebrow eyebrow-dim font-normal">${esc(t('Date'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Amount'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Method'))}</th>
              <th scope="col" class="py-2 pr-3 eyebrow eyebrow-dim font-normal">${esc(t('Status'))}</th>
            </tr>
          </thead>
          <tbody>
            ${w.requests.map((r) => `
              <tr class="border-t border-border/60">
                <td class="py-2.5 px-3 whitespace-nowrap text-text-dim tabular-nums">${formatDate(r.createdAt)}</td>
                <td class="py-2.5 pr-3 whitespace-nowrap tabular-nums">${formatMoney(r.amountCents, r.currency)}</td>
                <td class="py-2.5 pr-3 whitespace-nowrap text-text-dim">${esc(r.payoutMethod ?? '—')}</td>
                <td class="py-2.5 pr-3 whitespace-nowrap">
                  <span class="chip text-[12px]">${esc(statusLabel(r.status))}</span>
                  ${r.reason ? `<span class="block text-[12px] text-text-dim">${esc(r.reason)}</span>` : ''}
                </td>
              </tr>`).join('')}
          </tbody>
        </table>
      </div>
    </section>`;

  return `
    <div class="grid gap-6 max-w-[620px]">
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Balance'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          <div class="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span class="grid">
              <span class="eyebrow eyebrow-dim">${esc(t('Available'))}</span>
              <span class="font-heading text-2xl text-gold tabular-nums">${formatMoney(spendable, w.currency)}</span>
            </span>
            ${w.pendingCents > 0 ? `
              <span class="grid">
                <span class="eyebrow eyebrow-dim">${esc(t('Already requested'))}</span>
                <span class="font-heading text-2xl tabular-nums">${formatMoney(w.pendingCents, w.currency)}</span>
              </span>` : ''}
            <span class="grid">
              <span class="eyebrow eyebrow-dim">${esc(t('Minimum payout'))}</span>
              <span class="font-heading text-2xl tabular-nums">${formatMoney(w.minimumCents, w.currency)}</span>
            </span>
          </div>
          ${blocked ? `<div class="notice-warn text-[13px]">${blocked}</div>` : ''}
        </div>
      </section>

      ${blocked ? '' : `
      <section class="grid gap-3">
        <h3 class="font-heading text-lg text-gold">${esc(t('Request payout'))}</h3>
        <div class="frame-pewter p-3.5 grid gap-3">
          ${field('set-wd-amount', t('Amount in {currency}', { currency: w.currency }),
            `<input id="set-wd-amount" class="${INPUT_CLASS}" type="number" inputmode="decimal"
                    min="${(w.minimumCents / 100).toFixed(2)}" max="${(spendable / 100).toFixed(2)}" step="0.01" />`)}
          ${w.methods.length > 0 ? field('set-wd-method', t('Method'),
            `<select id="set-wd-method" class="${INPUT_CLASS}">
               ${w.methods.map((m) => `<option value="${esc(m.id)}">${esc(m.label)}</option>`).join('')}
             </select>`) : ''}
          ${field('set-wd-details', t('Where to send it'),
            `<input id="set-wd-details" class="${INPUT_CLASS}" type="text" maxlength="200"
                    placeholder="${esc(w.methods.length > 0 ? (w.methods[0].receiver === 'iban' ? t('IBAN') : t('Card number')) : t('IBAN or card number'))}" />`)}
          <div class="flex items-center gap-2 pt-1">
            <button id="set-wd-save" type="button" class="btn btn-gold px-5">${esc(t('Request payout'))}</button>
            <span id="set-wd-msg" class="text-[13px] text-text-dim" role="status" aria-live="polite"></span>
          </div>
        </div>
      </section>`}

      ${history}
    </div>`;
}

/**
 * The one honest sentence for whichever reason applies, or null when a payout
 * can actually be requested. Order matters: a switched-off build is not worth
 * explaining KYC over, and having no balance is not the player's mistake.
 */
export function withdrawalBlockReason(w: WithdrawalOverview, spendable: number): string | null {
  if (!w.enabled) return esc(t('Payouts are switched off on this build.'));
  if (spendable <= 0) {
    return esc(t('Your withdrawable balance is zero. Deposit Ember and it becomes available for withdrawal.'));
  }
  // Core asks for APPROVED only while verification is switched on.
  if (w.kycRequired !== false && w.kycStatus !== 'APPROVED') {
    return esc(t('Verify your identity in the Profile section before requesting a payout.'));
  }
  if (spendable < w.minimumCents) {
    return t('The minimum payout is {amount}.', { amount: formatMoney(w.minimumCents, w.currency) });
  }
  return null;
}

// ─── Shared states ───────────────────────────────────────────────────────────

export function skeleton(label: string): string {
  return `<p class="text-text-dim text-[14px]" role="status">${esc(label)}</p>`;
}

export function emptyState(title: string, body: string): string {
  return `
    <div class="frame-pewter p-6 text-center grid gap-2 max-w-[480px]">
      <p class="font-heading text-lg">${esc(title)}</p>
      <p class="text-[13px] text-text-dim">${esc(body)}</p>
    </div>`;
}

export function railMarkup(
  active: SectionId,
  features?: { withdrawals?: boolean; invoices?: boolean },
): string {
  const visible = SECTIONS.filter((s) => {
    if (s.id === 'withdrawal' && features && !features.withdrawals) return false;
    // 2026-10-19: the Invoices toggle now reaches players (feature.invoices).
    if (s.id === 'invoices' && features && !features.invoices) return false;
    return true;
  });
  return visible.map((s) => `
    <button type="button" role="tab" data-section="${s.id}" id="set-tab-${s.id}"
      aria-selected="${s.id === active}" aria-controls="set-panel"
      tabindex="${s.id === active ? '0' : '-1'}"
      class="settings-rail-item">${esc(t(s.label))}</button>`).join('');
}
